import { GetterTree, MutationTree, Module } from 'vuex';
import { RootState } from '../rootState';
import { ElementMenuItem } from '@/models/elementMenuItem';
import { SlotItem } from '@/models/slotItem';
import { PresetMenuItem } from '@/models/presets';
import Element, { ElementType } from '@/models/element';
import { IItemParams } from '@/models/itemParams';
import { CubeTexture } from '@babylonjs/core';

export interface Scene {
    selectedElementMenuItem: ElementMenuItem | null;
    slotItem: SlotItem | null;
    areFrontsVisible: boolean;
    tasks: number;
    allTasks: number;
    lastTaskPercentage: number;
    tasksPercentage: number;
    selectedPreset: PresetMenuItem | null;
    productLoaded: boolean;
    presetsLoaded: boolean;
    price: number;
    elements: Element[];
    backs: Element[];
    loadingMessage: string;
    loadingProductError: boolean;
    previousItemClicked: IItemParams | null;
    hdrTexture: CubeTexture | null;
}

const getters: GetterTree<Scene, RootState> = {
    scene(state): Scene { return state; },
    body(state): Element | undefined {
        return state.elements.find((x) => x.type === ElementType.BODY);
    }
};

const mutations: MutationTree<Scene> = {
    selectElementMenuItem(state, element: ElementMenuItem) {
        state.selectedElementMenuItem = element;
    },
    selectSlotItem(state, slot: SlotItem) {
        state.slotItem = slot;
    },
    clearSceneState(state) {
        state.selectedElementMenuItem = null;
        state.slotItem = null;
    },
    toggleFrontsVisibility(state) {
        state.areFrontsVisible = !state.areFrontsVisible;
    },
    setFrontsVisibility(state, visibility: boolean) {
        state.areFrontsVisible = visibility;
    },
    incrementTasks(state) {
        state.tasks++;
        state.allTasks++;
    },
    decrementTasks(state) {
        if (state.tasks > 0) {
            state.tasks--;
            if (state.tasks === 0) {
                state.allTasks = 0;
                state.lastTaskPercentage = 0;
            }
        }
    },
    selectPreset(state, preset: PresetMenuItem) {
        state.selectedPreset = preset;
    },
    setProductLoaded(state, loaded: boolean) {
        state.productLoaded = loaded;
    },
    setPresetsLoaded(state, loaded: boolean) {
        state.presetsLoaded = loaded;
    },
    setPrice(state, price: number) {
        state.price = price;
    },
    setElements(state, elements: Element[]) {
        state.elements = elements;
    },
    setBacks(state, backs: Element[]) {
        state.backs = backs;
    },
    showLoadingScreen(state, message: string) {
        state.loadingMessage = message;
    },
    hideLoadingScreen(state) {
        state.loadingMessage = '';
    },
    resetModel(state) { },
    setLoadingProductError(state, error: boolean) {
        state.loadingProductError = error;
    },
    setPreviousItemClicked(state, item: IItemParams) {
        state.previousItemClicked = item;
    },
    setHdrTexture(state, hdrtexture: CubeTexture) {
        state.hdrTexture = hdrtexture;
    },
    center(state) { },
    configurationChange(state) { },
};

const currentState: Scene = {
    selectedElementMenuItem: null,
    slotItem: null,
    areFrontsVisible: true,
    tasks: 0,
    allTasks: 0,
    selectedPreset: null,
    productLoaded: false,
    presetsLoaded: false,
    price: 0,
    elements: [],
    backs: [],
    loadingMessage: '',
    loadingProductError: false,
    previousItemClicked: null,
    hdrTexture: null,
    lastTaskPercentage: 0,
    get tasksPercentage(): number {
        const percentage = (this.allTasks - this.tasks) * 100 / this.allTasks;
        const fixedPercentage = Number(percentage.toFixed(0));
        const result = fixedPercentage > this.lastTaskPercentage ?
            fixedPercentage : this.lastTaskPercentage;
        this.lastTaskPercentage = result;
        return result;
    },
};

export const SceneModule: Module<Scene, RootState> = {
    state: currentState,
    getters,
    mutations,
};
